// typed: strict
import type { IconType } from '@factorialco/f0-react'
import { Bullet, Link, LinkRemove } from '@factorialco/f0-react/icons/app'

import type { NodeAttributesFragment } from 'generated/resources/graphql'

import {
  NODE_TYPES,
  NodeType,
  getTextFromNodeType,
  isNodeType,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'

export interface EquipmentRecord {
  id: string
  brand: string
  name: string
  typeName: string
  definedAt: string
  icon: IconType
  readonly: boolean
  inheritedFrom?: string
  nodeAttributeId: string
  definedAtNodeType: NodeType
  isOverride: boolean
  originalNodeAttributeId?: string
  [key: string]: unknown
}

export interface EquipmentConfig {
  id: string
  readonly: boolean
}

const getEquipmentInheritanceIcon = (
  type: NodeType,
  isInherited: boolean,
  isOverride: boolean,
  _currentNodeLevel: string,
  _definedAtLevel: string
): IconType => {
  if (isOverride) {
    return LinkRemove
  }

  if (isInherited) {
    return Link
  }

  return Bullet
}

interface NodeAttribute {
  id: string
  type: string
  takesPrecedence: boolean
  serializedValue?: string | null
  definedAt: string
  attributeId?: string | null
}

export const mapEquipmentData = (
  attributes: NodeAttribute[],
  nodeId: string,
  setCurrentEquipment?: (equipment: EquipmentConfig[]) => void
): EquipmentRecord[] => {
  const allEquipment = attributes
    .filter((attr) => attr.type.toLowerCase() === 'it_management')
    .filter((attr) => attr.takesPrecedence)
    .map((attr) => {
      const parsed = JSON.parse(attr.serializedValue || '{}')
      const normalizedType = normalizeNodeType(attr.definedAt)
      const type: NodeType = isNodeType(normalizedType) ? normalizedType : NODE_TYPES.ROOT

      const attributeId = attr.attributeId
        ? typeof attr.attributeId === 'string'
          ? attr.attributeId
          : attr.attributeId
        : '0'
      const isOverride = parsed?.isOverride === true
      const currentLevel = getTextFromNodeType(normalizeNodeType(nodeId))
      const definedAtLevel = getTextFromNodeType(normalizeNodeType(attr.definedAt))
      const isInherited = currentLevel !== definedAtLevel

      return {
        id: parsed?.it_asset_model_id ?? attributeId.toString(),
        brand: parsed?.brand ?? '-',
        name: parsed?.name ?? '-',
        typeName: parsed?.typeName ?? '-',
        definedAt: getTextFromNodeType(type),
        icon: getEquipmentInheritanceIcon(
          type,
          isInherited,
          isOverride,
          currentLevel,
          definedAtLevel
        ),
        readonly: currentLevel !== definedAtLevel,
        inheritedFrom: isInherited
          ? getTextFromNodeType(type)
          : isOverride
            ? parsed?.originalInheritedFrom
            : undefined,
        nodeAttributeId: attr.id,
        definedAtNodeType: type,
        isOverride,
        originalNodeAttributeId: parsed?.originalNodeAttributeId,
      }
    })

  const overriddenNodeAttributeIds = new Set(
    allEquipment
      .filter((eq) => eq.isOverride && eq.originalNodeAttributeId)
      .map((eq) => eq.originalNodeAttributeId)
  )

  const equipment = allEquipment.filter((eq) => !overriddenNodeAttributeIds.has(eq.nodeAttributeId))

  if (setCurrentEquipment) {
    setCurrentEquipment(
      equipment.map((equipmentItem) => ({
        id: equipmentItem.id,
        readonly: equipmentItem.readonly,
      }))
    )
  }

  return equipment
}

interface PaginationData {
  jobCatalog: {
    nodeAttributesConnection: {
      nodes: NodeAttribute[]
    }
  }
}

export const calculateEquipmentPaginationTotal = (data: PaginationData): number => {
  return (
    data.jobCatalog.nodeAttributesConnection.nodes
      .filter((attr) => attr.type.toLowerCase() === 'it_management')
      .filter((attr) => attr.takesPrecedence).length ?? 0
  )
}

export const isEquipmentRecord = (record: unknown): record is EquipmentRecord => {
  return (
    typeof record === 'object' &&
    record !== null &&
    'brand' in record &&
    'name' in record &&
    'typeName' in record
  )
}

export interface EquipmentDisplayItem {
  id: string
  label: string
  typeName: string
  isInherited: boolean
  inheritedFromName?: string
  isOverride: boolean
}

export const getEquipmentForDisplay = (
  attributes: NodeAttributesFragment[],
  currentNodeType: string
): EquipmentDisplayItem[] => {
  const itAttributes = attributes.filter(
    (attr) => attr.type.toLowerCase() === 'it_management' && attr.takesPrecedence
  )

  const allItems = itAttributes.flatMap((attr): EquipmentDisplayItem[] => {
    try {
      const parsed = JSON.parse(attr.serializedValue ?? '{}')
      const brand: string = parsed?.brand ?? ''
      const name: string = parsed?.name ?? ''
      const typeName: string = parsed?.typeName ?? ''
      const isOverride = parsed?.isOverride === true

      const normalizedDefinedAt = normalizeNodeType(attr.definedAt)
      const normalizedCurrent = normalizeNodeType(currentNodeType)
      const isInherited = normalizedDefinedAt !== normalizedCurrent

      return [
        {
          id: `${attr.id}`,
          label: [brand, name].filter(Boolean).join(' ') || '-',
          typeName,
          isInherited,
          inheritedFromName: isInherited ? (attr.definedAtNode.name ?? undefined) : undefined,
          isOverride,
        },
      ]
    } catch {
      return []
    }
  })

  const overriddenIds = new Set(allItems.filter((item) => item.isOverride).map((item) => item.id))

  return allItems.filter((item) => !overriddenIds.has(item.id) || item.isOverride)
}
